import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import { useFetch } from "../../../hooks";
import { ToolsRunLineItemProps } from "../../../interfaces/interfaces";
import { ToolsRunStatusBadge } from "./ToolsRunStatusBadge";
import { formatToolsRunQuantity } from "./toolsRunFormatters";

type TimelineMode = "tool" | "well";

interface TimelineResponse {
  success?: boolean;
  succes?: boolean;
  data: ToolsRunLineItemProps[];
}

interface Props {
  row: ToolsRunLineItemProps | null;
  onClose: () => void;
}

const TOOL_COLORS = ["#ED8B00", "#3B7C8F", "#557A46", "#A55757", "#7565A8"];

const dateParts = (value: string | null) => {
  if (!value) return { month: "—", day: "—", year: "", weekday: "" };
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return { month: "—", day: "—", year: "", weekday: "" };
  }
  return {
    month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    day: date.toLocaleDateString("en-US", { day: "2-digit" }),
    year: date.toLocaleDateString("en-US", { year: "numeric" }),
    weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
  };
};

const colorForProduct = (productId: number | null) =>
  TOOL_COLORS[Math.abs(productId || 0) % TOOL_COLORS.length];

export const ToolsRunTimelineDrawer = ({ row, onClose }: Props) => {
  const [mode, setMode] = useState<TimelineMode>("tool");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setMode(row?.productId ? "tool" : "well");
    setPage(0);
  }, [row?.id, row?.productId]);

  const filters = useMemo(
    () => ({
      clientId: row?.clientId,
      wellId: row?.wellId,
      productId: mode === "tool" ? row?.productId : undefined,
      latestByPartNumber: mode === "well" ? "true" : undefined,
    }),
    [mode, row?.clientId, row?.productId, row?.wellId],
  );
  const paginationOptions = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize],
  );
  const { data, error, isLoading, pagination } = useFetch<TimelineResponse>(
    row?.clientId && row?.wellId ? "tools-run-paginate" : undefined,
    paginationOptions,
    undefined,
    undefined,
    filters,
  );
  const events = data?.data || [];
  const clientName = row?.client?.name || row?.rawShipToClient || "Client";
  const wellName = row?.well?.name || row?.rawShipToWell || "Well";
  const toolName = row?.product?.name || row?.toolNameRaw || "Tool";

  const changeMode = (_: React.SyntheticEvent, value: TimelineMode) => {
    setMode(value);
    setPage(0);
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(row)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 640, md: 720, lg: 760 },
          maxWidth: "100vw",
          bgcolor: "#F4F3EF",
          overflowX: "hidden",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          px: 3,
          pt: 3,
          pb: 2.5,
          color: "white",
          bgcolor: "#303A48",
          backgroundImage: "linear-gradient(125deg, #303A48, #445161)",
          "&:after": {
            content: '""',
            position: "absolute",
            width: 140,
            height: 140,
            borderRadius: "50%",
            border: "26px solid rgba(237,139,0,.18)",
            right: -48,
            top: -70,
          },
        }}
      >
        <IconButton
          aria-label="Close timeline"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1,
            color: "white",
            bgcolor: "rgba(255,255,255,.1)",
            "&:hover": { bgcolor: "rgba(255,255,255,.18)" },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TimelineRoundedIcon sx={{ color: "#F6A72D" }} />
          <Typography variant="overline" sx={{ letterSpacing: 1.4, color: "#D7DCE3", fontWeight: 800 }}>
            {mode === "tool" ? "Usage timeline" : "Wellbore tracker"}
          </Typography>
        </Stack>
        <Typography variant="h5" sx={{ pr: 5, fontWeight: 850, letterSpacing: "-.35px" }}>
          {mode === "tool" ? toolName : wellName}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.6, color: "#D5DAE1" }}>
          {clientName} · {wellName}
        </Typography>
      </Box>

      <Tabs
        value={mode}
        onChange={changeMode}
        variant="fullWidth"
        sx={{
          px: 2,
          bgcolor: "white",
          borderBottom: "1px solid #E5E3DC",
          "& .MuiTab-root": { minHeight: 54, fontSize: 12, fontWeight: 800 },
          "& .MuiTabs-indicator": { bgcolor: "#ED8B00", height: 3 },
        }}
      >
        <Tab value="tool" label="Tool history" disabled={!row?.productId} />
        <Tab value="well" label="Current wellbore" />
      </Tabs>

      <Box sx={{ flex: 1, minWidth: 0, overflowX: "hidden", overflowY: "auto", px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {mode === "tool"
            ? "History for this specific tool in this specific client and well, newest first."
            : "The latest recorded installation for each part number in this well. Each tool appears once."}
        </Typography>

        {error ? <Alert severity="error">The usage timeline could not be loaded.</Alert> : null}
        {!error && isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", minHeight: 260 }}>
            <CircularProgress size={32} />
          </Box>
        ) : null}
        {!error && !isLoading && !events.length ? (
          <Box sx={{ display: "grid", placeItems: "center", textAlign: "center", minHeight: 280, color: "#8A919C" }}>
            <Box>
              <Inventory2OutlinedIcon sx={{ fontSize: 38, color: "#B8BDC5", mb: 1 }} />
              <Typography fontWeight={800}>{mode === "tool" ? "No usage records found" : "No current tools found"}</Typography>
              <Typography variant="body2">{mode === "tool" ? "There are no matching runs in this timeline." : "No installed tools have been recorded for this well."}</Typography>
            </Box>
          </Box>
        ) : null}

        {!error && !isLoading && mode === "tool" ? (
          <Box sx={{ position: "relative" }}>
            {events.map((event, index) => {
              const date = dateParts(event.date);
              const accent = "#ED8B00";
              return (
                <Box
                  key={event.id}
                  sx={{
                    position: "relative",
                    display: "grid",
                    gridTemplateColumns: "58px 18px 1fr",
                    gap: 1.25,
                    pb: index === events.length - 1 ? 0 : 2.25,
                  }}
                >
                  <Box sx={{ textAlign: "right", pt: 0.2 }}>
                    <Typography sx={{ color: "#6D7480", fontSize: 10, lineHeight: 1, letterSpacing: 0.8, fontWeight: 850 }}>{date.month}</Typography>
                    <Typography sx={{ color: "#303A48", fontSize: 22, lineHeight: 1.15, fontWeight: 900 }}>{date.day}</Typography>
                    <Typography sx={{ color: "#9399A2", fontSize: 10, fontWeight: 700 }}>{date.year}</Typography>
                  </Box>
                  <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
                    {index < events.length - 1 ? <Box sx={{ position: "absolute", top: 15, bottom: -18, width: 2, bgcolor: "#DCDAD3" }} /> : null}
                    <Box sx={{ mt: 0.5, zIndex: 1, width: 12, height: 12, borderRadius: "50%", bgcolor: accent, border: "3px solid #F4F3EF", boxShadow: `0 0 0 2px ${accent}` }} />
                  </Box>
                  <Box sx={{ minWidth: 0, p: 1.75, bgcolor: "white", border: "1px solid #E3E1D9", borderRadius: 2.25, boxShadow: "0 4px 14px rgba(47,56,68,.06)" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={850} noWrap>
                          {event.product?.name || event.toolNameRaw || "Unresolved tool"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {event.product?.partNumber || event.partNumber || "No part number"}
                        </Typography>
                      </Box>
                      <ToolsRunStatusBadge status={event.status} />
                    </Stack>
                    <Stack direction="row" spacing={2.5} sx={{ mt: 1.35 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Invoice</Typography>
                        <Typography variant="body2" fontWeight={750}>{event.invoice || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Quantity</Typography>
                        <Typography variant="body2" fontWeight={750}>{formatToolsRunQuantity(event.qty ?? event.qtyRaw)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Day</Typography>
                        <Typography variant="body2" fontWeight={750}>{date.weekday || "—"}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}

        {!error && !isLoading && mode === "well" ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 1.5,
            }}
          >
            {events.map((event) => {
              const date = dateParts(event.date);
              const accent = colorForProduct(event.productId);
              return (
                <Box
                  key={event.id}
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    bgcolor: "white",
                    border: "1px solid #E3E1D9",
                    borderTop: `4px solid ${accent}`,
                    borderRadius: 2.25,
                    boxShadow: "0 4px 14px rgba(47,56,68,.06)",
                  }}
                >
                  <Box sx={{ p: 1.75 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" sx={{ color: "#8A919C", fontSize: 9.5, lineHeight: 1.4, letterSpacing: 1, fontWeight: 850 }}>
                          Current tool
                        </Typography>
                        <Typography variant="body2" fontWeight={850} noWrap>
                          {event.product?.name || event.toolNameRaw || "Unresolved tool"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: accent, fontWeight: 800 }}>
                          {event.product?.partNumber || event.partNumber || "No part number"}
                        </Typography>
                      </Box>
                      <ToolsRunStatusBadge status={event.status} />
                    </Stack>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 1.75, pt: 1.5, borderTop: "1px solid #EFEEE9" }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Last installed</Typography>
                        <Typography variant="body2" fontWeight={750}>{date.month} {date.day}, {date.year}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Quantity</Typography>
                        <Typography variant="body2" fontWeight={750}>{formatToolsRunQuantity(event.qty ?? event.qtyRaw)}</Typography>
                      </Box>
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography variant="caption" color="text.secondary" display="block">Invoice</Typography>
                        <Typography variant="body2" fontWeight={750}>{event.invoice || "—"}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>

      <TablePagination
        component="div"
        count={Number(pagination.totalRecords || 0)}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
          bgcolor: "white",
          borderTop: "1px solid #E5E3DC",
          "& .MuiTablePagination-toolbar": { flexWrap: "wrap" },
        }}
      />
    </Drawer>
  );
};
